import { crmGetContactDisplayName } from './contact.helpers';

// Pure helpers for the contact summary card. Must not import vue-foundation, MobileBridge or Sales
// modals (CX bundle isolation); permission-aware helpers live in contact-summary.access.ts.

export interface IContactSummaryContact {
    id: string;
    avatarId?: string | null;
    displayName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    email?: string | null;
    /** Known false → skip the alert-note lookup; unknown (undefined) → look it up. */
    hasAlerts?: boolean | null;
}

export interface IContactAlertPreview {
    /** Most recent alert note, or null when the contact has none. */
    latest: { id: string; content: string; createdAt: string } | null;
    /** Alert notes only (plain notes are not counted). */
    count: number;
}

/** Most recent alert note plus the alert-note count, from a contact's full note list. */
export function summarizeAlertNotes(notes: { id: string; content: string; createdAt: string; isAlert: boolean }[]): IContactAlertPreview {
    const alerts = notes.filter(n => n.isAlert);
    const latest = alerts.reduce<(typeof alerts)[number] | null>(
        (best, n) => (!best || new Date(n.createdAt).getTime() > new Date(best.createdAt).getTime() ? n : best),
        null
    );
    return { latest: latest && { id: latest.id, content: latest.content, createdAt: latest.createdAt }, count: alerts.length };
}

export interface IContactSummaryBooking {
    id: string;
    itemId: string;
    start: string;
    end: string | null;
}

export interface IContactSummaryData {
    contactId?: string;
    totalSpend: number | null;
    lastClosedSale: { id: string; closedAt: string; amount: number } | null;
    lastBooking: IContactSummaryBooking | null;
    nextBooking: IContactSummaryBooking | null;
    bookingsUnavailable: boolean;
}

export type ContactLastActivity =
    | { kind: 'sale'; id: string; at: string; amount: number }
    | { kind: 'booking'; id: string; itemId: string; at: string; inProgress: boolean };

/** Later of last closed sale / last booking. A booking that was the sale's own tab counts as the sale. */
export function pickLastActivity(
    summary: Pick<IContactSummaryData, 'lastClosedSale' | 'lastBooking'> | null | undefined,
    asOf: string
): ContactLastActivity | null {
    const sale = summary?.lastClosedSale ?? null;
    const booking = summary?.lastBooking ?? null;
    if (!sale && !booking) return null;

    const asSale = (): ContactLastActivity => ({ kind: 'sale', id: sale!.id, at: sale!.closedAt, amount: sale!.amount });
    const asBooking = (): ContactLastActivity => ({
        kind: 'booking',
        id: booking!.id,
        itemId: booking!.itemId,
        at: booking!.start,
        inProgress: booking!.end != null && new Date(booking!.end).getTime() > new Date(asOf).getTime()
    });

    if (sale && !booking) return asSale();
    if (booking && !sale) return asBooking();
    if (booking!.id === sale!.id) return asSale();
    return new Date(booking!.start).getTime() > new Date(sale!.closedAt).getTime() ? asBooking() : asSale();
}

export function contactIdentityLines(c: Pick<IContactSummaryContact, 'displayName' | 'firstName' | 'lastName' | 'company'>): {
    primary: string;
    secondary: string | null;
} {
    const primary = crmGetContactDisplayName(c, false) ?? c.company ?? 'Unnamed contact';
    const secondary = c.company && c.company !== primary ? c.company : null;
    return { primary, secondary };
}

export interface IRectLike {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

const PLACE_MARGIN = 8;

/** Below + left-aligned to the anchor; flips above when it doesn't fit; x clamped into the viewport. */
export function placeFloatingCard(
    anchor: IRectLike,
    size: { width: number; height: number },
    viewport: { width: number; height: number },
    gap = 6
): { left: number; top: number } {
    let top = anchor.bottom + gap;
    if (top + size.height > viewport.height - PLACE_MARGIN && anchor.top - gap - size.height >= PLACE_MARGIN) {
        top = anchor.top - gap - size.height;
    }
    const maxLeft = Math.max(PLACE_MARGIN, viewport.width - size.width - PLACE_MARGIN);
    const left = Math.min(Math.max(anchor.left, PLACE_MARGIN), maxLeft);
    return { left, top };
}
